import { useState, useEffect, useMemo } from 'react'
import { generateClient } from 'aws-amplify/api'
import { getHRDashboardData, getCompanyForHREmail } from '../graphql/queries'
import Sidebar from './Sidebar'
import Overview from './Overview'
import Heatmap from './Heatmap'
import Efficiencies from './Efficiencies'
import Archive from './Archive'
import Analytics from './Analytics'
import FlagModal from './FlagModal'
import './Dashboard.css'

function DashboardContent({ user, session, onSignOut }) {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [currentView, setCurrentView] = useState('overview')
  const [selectedFlag, setSelectedFlag] = useState(null)
  const [totalThreads, setTotalThreads] = useState(0)
  const [userEmail, setUserEmail] = useState(null)

  // Initialize GraphQL client - recreate when session changes to ensure auth is current
  const client = useMemo(() => {
    if (!session) return null
    try {
      const gqlClient = generateClient()
      console.log('GraphQL client initialized with session')
      return gqlClient
    } catch (error) {
      console.error('Error initializing GraphQL client:', error)
      return null
    }
  }, [session])

  const fetchDashboardData = async (email) => {
    if (!client) {
      setError('GraphQL client not initialized')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      console.log('Fetching dashboard data for:', email)

      // First, get the company ID for this HR user
      const companyResponse = await client.graphql({
        query: getCompanyForHREmail,
        variables: { email: email }
      })

      console.log('Company response:', JSON.stringify(companyResponse, null, 2))

      if (companyResponse.errors) {
        console.error('GraphQL errors:', JSON.stringify(companyResponse.errors, null, 2))
        const firstError = companyResponse.errors[0]
        const errorMsg = firstError?.message || firstError?.errorType || JSON.stringify(firstError) || 'Failed to fetch company data'
        console.error('Full error details:', firstError)
        throw new Error(`GraphQL Error: ${errorMsg}`)
      }

      const company = companyResponse.data?.getCompanyForHREmail
      if (!company) {
        console.error('No company found for email:', email)
        console.error('Full response:', JSON.stringify(companyResponse, null, 2))
        throw new Error(`You are not registered as an HR representative for any company. Email: ${email}`)
      }

      const cid = company.companyId
      setCompanyId(cid)

      // Then fetch dashboard data (pass email to bypass identity claims issue)
      const response = await client.graphql({
        query: getHRDashboardData,
        variables: { companyId: cid, userEmail: email }
      })

      console.log('Dashboard data response:', response)

      if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to fetch dashboard data')
      }

      setDashboardData(response.data?.getHRDashboardData)
      
      // Note: Thread count would need to be fetched separately if needed
      // For now, we'll use totalThreads from the API response if available
      if (response.data?.getHRDashboardData?.totalThreads) {
        setTotalThreads(response.data.getHRDashboardData.totalThreads)
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err)
      const errorMessage = err.message || err.errors?.[0]?.message || 'Failed to load dashboard data'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Only fetch data if we have a user and session
    if (!user || !session) {
      setError('User not authenticated')
      setLoading(false)
      return
    }

    const getUserEmail = async () => {
      try {
        // In Amplify v6, fetchUserAttributes returns an object with attribute names as keys
        const { fetchUserAttributes } = await import('aws-amplify/auth')
        const attributes = await fetchUserAttributes()
        console.log('User attributes:', attributes)
        
        // Access email directly from the attributes object
        const email = attributes.email
        
        if (email) {
          console.log('User authenticated, fetching data for:', email)
          setUserEmail(email)
          await fetchDashboardData(email)
        } else {
          console.error('No email found in user attributes:', attributes)
          setError('Unable to get user email. Please try logging in again.')
          setLoading(false)
        }
      } catch (err) {
        console.error('Error getting user attributes:', err)
        setError(`Failed to get user information: ${err.message || err.name || 'Unknown error'}. Please try logging in again.`)
        setLoading(false)
      }
    }

    getUserEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session]) // fetchDashboardData is stable, no need to include it

  const handleFlagClick = async (flagId) => {
    try {
      const { getFlagDetails } = await import('../graphql/queries')
      const response = await client.graphql({
        query: getFlagDetails,
        variables: { flagId }
      })
      setSelectedFlag(response.data.getFlagDetails)
    } catch (err) {
      console.error('Error loading flag details:', err)
      alert('Failed to load flag details')
    }
  }

  const closeModal = () => {
    setSelectedFlag(null)
  }

  const refreshDashboard = async () => {
    try {
      const { fetchUserAttributes } = await import('aws-amplify/auth')
      const attributes = await fetchUserAttributes()
      const email = attributes.email
      if (email) {
        fetchDashboardData(email)
      }
    } catch (err) {
      console.error('Error refreshing dashboard:', err)
    }
  }

  // Show error if we have one
  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <div className="empty-state-icon">⚠️</div>
          <h3>Error</h3>
          <p>{error}</p>
          <details style={{ marginTop: '20px', textAlign: 'left', maxWidth: '600px' }}>
            <summary>Debug Info</summary>
            <pre style={{ fontSize: '12px', overflow: 'auto', marginTop: '10px' }}>
              {JSON.stringify({ user: user ? 'exists' : 'null', loading, hasClient: !!client, hasData: !!dashboardData }, null, 2)}
            </pre>
          </details>
          <button onClick={() => window.location.reload()} style={{ marginTop: '20px', width: 'auto', padding: '10px 20px' }}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Show loading while fetching data
  if (loading || !dashboardData) {
    return (
      <div className="loading-container">
        <div className="loading">Loading Olivea HR Dashboard...</div>
        {process.env.NODE_ENV === 'development' && (
          <div style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
            User: {user ? 'authenticated' : 'not authenticated'}<br/>
            Loading: {loading ? 'yes' : 'no'}<br/>
            Data: {dashboardData ? 'loaded' : 'not loaded'}
          </div>
        )}
      </div>
    )
  }

  // If no user at this point, something went wrong (Authenticator should handle this)
  if (!user) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>Authentication Error</h3>
          <p>Please refresh the page and try logging in again.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '20px', width: 'auto', padding: '10px 20px' }}>
            Refresh
          </button>
        </div>
      </div>
    )
  }

  const getViewTitle = () => {
    const titles = {
      'overview': 'Overview',
      'heatmap': 'Escalations',
      'analytics': 'Analytics',
      'archive': 'Archive',
      'efficiencies': 'Efficiencies'
    }
    return titles[currentView] || 'Overview'
  }

  return (
    <div className="dashboard-layout">
      <Sidebar 
        currentView={currentView} 
        onViewChange={setCurrentView}
      />
      
      <div className="main-content">
        <div className="top-bar">
          <h2>{getViewTitle()}</h2>
            <div className="user-section">
              <div className="user-info">
                <div className="user-email">{userEmail || user.username || 'User'}</div>
              </div>
              <button className="logout-btn" onClick={onSignOut}>Sign Out</button>
            </div>
        </div>

        <div className="content-container">
          {currentView === 'overview' && (
            <Overview 
              data={dashboardData} 
              totalThreads={totalThreads}
            />
          )}
          {currentView === 'heatmap' && (
            <Heatmap 
              data={dashboardData}
              onFlagClick={handleFlagClick}
            />
          )}
          {currentView === 'efficiencies' && (
            <Efficiencies 
              data={dashboardData}
              companyId={companyId}
              userEmail={userEmail}
            />
          )}
          {currentView === 'archive' && (
            <Archive 
              companyId={companyId}
              userEmail={userEmail}
            />
          )}
          {currentView === 'analytics' && (
            <Analytics 
              companyId={companyId}
              userEmail={userEmail}
            />
          )}
        </div>
      </div>

      {selectedFlag && (
        <FlagModal 
          flag={selectedFlag}
          onClose={closeModal}
          onUpdate={refreshDashboard}
        />
      )}
    </div>
  )
}

// DashboardContent is now exported and used directly from App.jsx
export default DashboardContent
